import React, { useState } from 'react';
import {
  Calendar,
  Clock,
  Users,
  MapPin,
  Plus,
  Edit2,
  Trash2,
  ChevronLeft,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useTranslation } from '../../i18n';

interface Schedule {
  id: string;
  name: string;
  guardId: string;
  guardName: string;
  route: string;
  startTime: string;
  endTime: string;
  frequency: 'daily' | 'weekly' | 'custom';
  checkpoints: string[];
  status: 'active' | 'inactive' | 'completed';
  days: string[];
}

interface NewSchedule {
  name: string;
  guardId: string;
  guardName: string;
  route: string;
  startTime: string;
  endTime: string;
  frequency: 'daily' | 'weekly' | 'custom';
  checkpoints: string[];
  days: string[];
}

export const SchedulesPage: React.FC = () => {
  const { t, lang } = useTranslation();

  // Translation maps for day abbreviations, full day names and shift/frequency labels
  const dayLabel = (day: string): string => {
    const map: Record<string, [string, string]> = {
      Mon: ['schedules.dayMon', 'Mon'],
      Tue: ['schedules.dayTue', 'Tue'],
      Wed: ['schedules.dayWed', 'Wed'],
      Thu: ['schedules.dayThu', 'Thu'],
      Fri: ['schedules.dayFri', 'Fri'],
      Sat: ['schedules.daySat', 'Sat'],
      Sun: ['schedules.daySun', 'Sun'],
    };
    const entry = map[day];
    return entry ? t(entry[0], entry[1]) : day;
  };

  const frequencyLabel = (frequency: string): string => {
    const map: Record<string, [string, string]> = {
      daily: ['schedules.frequencyDaily', 'Daily'],
      weekly: ['schedules.frequencyWeekly', 'Weekly'],
      custom: ['schedules.frequencyCustom', 'Custom'],
    };
    const entry = map[frequency];
    return entry ? t(entry[0], entry[1]) : frequency;
  };

  const formatTime = (time: string): string => {
    const [h, m] = time.split(':').map((v) => parseInt(v, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return time;
    const d = new Date();
    d.setHours(h, m, 0, 0);
    return new Intl.DateTimeFormat(lang, { hour: 'numeric', minute: '2-digit' }).format(d);
  };
  const [schedules, setSchedules] = useState<Schedule[]>([
    {
      id: '1',
      name: 'Morning Patrol - Building A',
      guardId: 'G001',
      guardName: 'John Doe',
      route: 'Route A',
      startTime: '06:00',
      endTime: '14:00',
      frequency: 'daily',
      checkpoints: ['Main Entrance', 'Lobby', 'All Floors', 'Parking'],
      status: 'active',
      days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    },
    {
      id: '2',
      name: 'Night Shift - Full Campus',
      guardId: 'G002',
      guardName: 'Jane Smith',
      route: 'Route B',
      startTime: '22:00',
      endTime: '06:00',
      frequency: 'daily',
      checkpoints: ['All Buildings', 'Perimeter', 'Parking Areas'],
      status: 'active',
      days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    },
    {
      id: '3',
      name: 'Weekend Security',
      guardId: 'G003',
      guardName: 'Mike Johnson',
      route: 'Route C',
      startTime: '08:00',
      endTime: '20:00',
      frequency: 'weekly',
      checkpoints: ['Main Areas', 'Emergency Exits'],
      status: 'active',
      days: ['Sat', 'Sun'],
    },
  ]);

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState<'list' | 'calendar'>('list');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);

  // Form state for new/edit schedule
  const [formData, setFormData] = useState<NewSchedule>({
    name: '',
    guardId: '',
    guardName: '',
    route: '',
    startTime: '08:00',
    endTime: '17:00',
    frequency: 'daily',
    checkpoints: [],
    days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
  });

  // Available guards and routes
  const availableGuards = [
    { id: 'G001', name: 'John Doe' },
    { id: 'G002', name: 'Jane Smith' },
    { id: 'G003', name: 'Mike Johnson' },
    { id: 'G004', name: 'Sarah Williams' },
    { id: 'G005', name: 'Robert Brown' },
  ];

  const availableRoutes = [
    { id: 'route-a', name: 'Route A - Building Main' },
    { id: 'route-b', name: 'Route B - Perimeter' },
    { id: 'route-c', name: 'Route C - Security Zones' },
    { id: 'route-d', name: 'Route D - Parking Areas' },
  ];

  const allCheckpoints = [
    'Main Entrance', 'Lobby', 'Emergency Exits', 'Parking Areas',
    'Server Room', 'Reception Area', 'Loading Dock', 'Roof Access',
    'Generator Room', 'Executive Floor', 'Research Lab', 'Vault Area'
  ];

  const allDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getStatusBadgeClass = (status: Schedule['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'completed':
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + (direction === 'next' ? 1 : -1), 1));
  };

  const handleAddSchedule = () => {
    setEditingSchedule(null);
    setFormData({
      name: '',
      guardId: '',
      guardName: '',
      route: '',
      startTime: '08:00',
      endTime: '17:00',
      frequency: 'daily',
      checkpoints: [],
      days: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']
    });
    setShowAddModal(true);
  };

  const handleEditSchedule = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    setFormData({
      name: schedule.name,
      guardId: schedule.guardId,
      guardName: schedule.guardName,
      route: schedule.route,
      startTime: schedule.startTime,
      endTime: schedule.endTime,
      frequency: schedule.frequency,
      checkpoints: schedule.checkpoints,
      days: schedule.days
    });
    setShowAddModal(true);
  };

  const handleDeleteSchedule = (scheduleId: string) => {
    if (confirm(t('schedules.confirmDelete', 'Are you sure you want to delete this schedule?'))) {
      setSchedules(schedules.filter(s => s.id !== scheduleId));
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.guardId || !formData.route || formData.checkpoints.length === 0) {
      alert(t('schedules.fillRequired', 'Please fill in all required fields'));
      return;
    }

    const newSchedule: Schedule = {
      id: editingSchedule ? editingSchedule.id : `schedule-${Date.now()}`,
      name: formData.name,
      guardId: formData.guardId,
      guardName: formData.guardName,
      route: formData.route,
      startTime: formData.startTime,
      endTime: formData.endTime,
      frequency: formData.frequency,
      checkpoints: formData.checkpoints,
      days: formData.days,
      status: 'active'
    };

    if (editingSchedule) {
      setSchedules(schedules.map(s => s.id === editingSchedule.id ? newSchedule : s));
    } else {
      setSchedules([...schedules, newSchedule]);
    }

    setShowAddModal(false);
  };

  const handleGuardChange = (guardId: string) => {
    const guard = availableGuards.find(g => g.id === guardId);
    setFormData({
      ...formData,
      guardId,
      guardName: guard?.name || ''
    });
  };

  const handleCheckpointToggle = (checkpoint: string) => {
    const isSelected = formData.checkpoints.includes(checkpoint);
    if (isSelected) {
      setFormData({
        ...formData,
        checkpoints: formData.checkpoints.filter(c => c !== checkpoint)
      });
    } else {
      setFormData({
        ...formData,
        checkpoints: [...formData.checkpoints, checkpoint]
      });
    }
  };

  const handleDayToggle = (day: string) => {
    const isSelected = formData.days.includes(day);
    if (isSelected) {
      setFormData({
        ...formData,
        days: formData.days.filter(d => d !== day)
      });
    } else {
      setFormData({
        ...formData,
        days: [...formData.days, day]
      });
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{t('schedules.title', 'Patrol Schedules')}</h1>
          <p className="text-gray-600 mt-1">{t('schedules.subtitle', 'Assign guards to shifts and checkpoints')}</p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex rounded-md border border-gray-300">
            <button
              onClick={() => setView('list')}
              className={cn(
                "px-3 py-1 text-sm",
                view === 'list'
                  ? "bg-gray-100 text-gray-800"
                  : "text-gray-600 hover:bg-gray-50"
              )}
            >
              {t('schedules.listView', 'List View')}
            </button>
            <button
              onClick={() => setView('calendar')}
              className={cn(
                "px-3 py-1 text-sm border-l",
                view === 'calendar'
                  ? "bg-gray-100 text-gray-800"
                  : "text-gray-600 hover:bg-gray-50"
              )}
            >
              {t('schedules.calendar', 'Calendar')}
            </button>
          </div>

          <button
            onClick={handleAddSchedule}
            className="flex items-center gap-2 px-4 py-2 text-sm rounded-md transition-opacity hover:opacity-90 brand-green-gradient"
          >
            <Plus className="w-4 h-4" />
            {t('schedules.addSchedule', 'Add Schedule')}
          </button>
        </div>
      </div>

      {view === 'list' ? (
        /* List View */
        <div className="space-y-4">
          {schedules.map((schedule) => (
            <div key={schedule.id} className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-800">{schedule.name}</h3>
                      <span className={cn(
                        "px-2.5 py-0.5 rounded-md text-xs font-medium border",
                        getStatusBadgeClass(schedule.status)
                      )}>
                        {t(`status.${schedule.status}`, schedule.status)}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Users className="w-4 h-4" />
                          <span>{schedule.guardName} ({schedule.guardId})</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-4 h-4" />
                          <span>{formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}</span>
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>{frequencyLabel(schedule.frequency)}</span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-start gap-2 text-gray-600">
                          <MapPin className="w-4 h-4 mt-0.5" />
                          <div>
                            <p className="font-medium">{schedule.route}</p>
                            <p className="text-xs">{schedule.checkpoints.join(' → ')}</p>
                          </div>
                        </div>
                        <div className="flex gap-1 flex-wrap">
                          {schedule.days.map((day) => (
                            <span key={day} className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                              {dayLabel(day)}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEditSchedule(schedule)}
                      className="p-2 text-gray-600 hover:bg-gray-100 rounded-md"
                      title={t('schedules.editSchedule', 'Edit Schedule')}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteSchedule(schedule.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                      title={t('schedules.deleteSchedule', 'Delete Schedule')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Calendar View */
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-800">
                {new Intl.DateTimeFormat(lang, { month: 'long', year: 'numeric' }).format(selectedDate)}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigateMonth('prev')}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => navigateMonth('next')}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>

          <div className="p-4">
            <div className="grid grid-cols-7 gap-1 mb-2">
              {[0, 1, 2, 3, 4, 5, 6].map((dow) => {
                // 2024-01-07 is a Sunday; offset gives each weekday for localized short names
                const ref = new Date(2024, 0, 7 + dow);
                const label = new Intl.DateTimeFormat(lang, { weekday: 'short' }).format(ref);
                return (
                  <div key={dow} className="text-center text-xs font-medium text-gray-600 py-2">
                    {label}
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {getDaysInMonth(selectedDate).map((day, index) => (
                <div
                  key={index}
                  className={cn(
                    "min-h-[80px] p-2 border rounded-md",
                    day ? "border-gray-200 hover:bg-gray-50" : "border-transparent"
                  )}
                >
                  {day && (
                    <>
                      <div className="text-sm font-medium text-gray-800 mb-1">{day}</div>
                      {/* Sample schedule indicators */}
                      {day % 3 === 0 && (
                        <div className="space-y-1">
                          <div className="text-xs bg-blue-100 text-blue-800 px-1 py-0.5 rounded truncate">
                            {t('schedules.morningPatrol', 'Morning Patrol')}
                          </div>
                        </div>
                      )}
                      {day % 5 === 0 && (
                        <div className="space-y-1">
                          <div className="text-xs bg-green-100 text-green-800 px-1 py-0.5 rounded truncate">
                            {t('schedules.nightShift', 'Night Shift')}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('schedules.activeSchedules', 'Active Schedules')}</p>
              <p className="text-xl font-bold text-gray-800">{schedules.filter(s => s.status === 'active').length}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Users className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('schedules.assignedGuards', 'Assigned Guards')}</p>
              <p className="text-xl font-bold text-gray-800">{new Set(schedules.map(s => s.guardId)).size}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 rounded-lg">
              <MapPin className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('schedules.activeRoutes', 'Active Routes')}</p>
              <p className="text-xl font-bold text-gray-800">{new Set(schedules.map(s => s.route)).size}</p>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('schedules.coverageGaps', 'Coverage Gaps')}</p>
              <p className="text-xl font-bold text-gray-800">2</p>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Schedule Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-800">
                {editingSchedule ? t('schedules.editSchedule', 'Edit Schedule') : t('schedules.addNewSchedule', 'Add New Schedule')}
              </h2>
            </div>

            <form onSubmit={handleFormSubmit} className="p-6 space-y-6">
              {/* Schedule Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('schedules.scheduleName', 'Schedule Name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('schedules.scheduleNamePlaceholder', 'e.g., Morning Patrol - Building A')}
                  required
                />
              </div>

              {/* Guard Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('schedules.assignedGuard', 'Assigned Guard')} *
                </label>
                <select
                  value={formData.guardId}
                  onChange={(e) => handleGuardChange(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">{t('schedules.selectGuard', 'Select a guard')}</option>
                  {availableGuards.map((guard) => (
                    <option key={guard.id} value={guard.id}>
                      {guard.name} ({guard.id})
                    </option>
                  ))}
                </select>
              </div>

              {/* Route Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('schedules.route', 'Route')} *
                </label>
                <select
                  value={formData.route}
                  onChange={(e) => setFormData({ ...formData, route: e.target.value })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">{t('schedules.selectRoute', 'Select a route')}</option>
                  {availableRoutes.map((route) => (
                    <option key={route.id} value={route.name}>
                      {route.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Time Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('schedules.startTime', 'Start Time')} *
                  </label>
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('schedules.endTime', 'End Time')} *
                  </label>
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('schedules.frequency', 'Frequency')}
                </label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as 'daily' | 'weekly' | 'custom' })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="daily">{t('schedules.frequencyDaily', 'Daily')}</option>
                  <option value="weekly">{t('schedules.frequencyWeekly', 'Weekly')}</option>
                  <option value="custom">{t('schedules.frequencyCustom', 'Custom')}</option>
                </select>
              </div>

              {/* Days of Week */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('schedules.daysOfWeek', 'Days of Week')}
                </label>
                <div className="grid grid-cols-7 gap-2">
                  {allDays.map((day) => (
                    <label key={day} className="flex items-center justify-center">
                      <input
                        type="checkbox"
                        checked={formData.days.includes(day)}
                        onChange={() => handleDayToggle(day)}
                        className="sr-only"
                      />
                      <div className={cn(
                        "w-12 h-12 rounded-lg border-2 flex items-center justify-center text-sm font-medium cursor-pointer transition-colors",
                        formData.days.includes(day)
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-gray-300 bg-white text-gray-600 hover:bg-gray-50"
                      )}>
                        {dayLabel(day)}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Checkpoints */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('domain.checkpoints', 'Checkpoints')} *
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-md p-3">
                  {allCheckpoints.map((checkpoint) => (
                    <label key={checkpoint} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={formData.checkpoints.includes(checkpoint)}
                        onChange={() => handleCheckpointToggle(checkpoint)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{checkpoint}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('schedules.selectedCount', 'Selected: {n} checkpoint(s)').replace('{n}', String(formData.checkpoints.length))}
                </p>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm rounded-md transition-opacity hover:opacity-90 brand-green-gradient"
                >
                  {editingSchedule ? t('schedules.updateSchedule', 'Update Schedule') : t('schedules.createSchedule', 'Create Schedule')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
